import { useMemo, useState, useEffect } from "react"
import { Controller, useWatch } from "react-hook-form"
import { getCurrencyFormat } from "src/utils/string"
import InputMoney from "../../InputMoney/InputMoney"
import DollarCircle from "src/shared/icons/DollarCircle"
import { div, times } from "src/shared/helpers/calculator"

type TipOptionsProps = {
    defaultValue?: number
    defaultMonto?: number
    options: number[]
    amount: number
    index: number
    error?: boolean
    control: any
    onChange: (value: number) => void
    onItemSelected: (value: boolean) => void
}

export const TipOptions = ({
    amount = 0,
    index,
    options = [],
    error = false,
    onChange,
    onItemSelected,
    control,
    defaultValue = 0,
    defaultMonto = 0,
}: TipOptionsProps) => {
    const [value, setValue] = useState<number>(0)
    const [key, setKey] = useState<string>("")

    const tipOptions = useMemo(() => {
        const list = options.map((option) => {
            return {
                label: `${option}%`,
                value: Number((times(div(option, 100), amount)).toFixed(2)),
            }
        })

        return [...list, { label: "Otro", value: 0 }]
    }, [options, amount])

    useEffect(() => {
        if (defaultValue === 0 && defaultMonto > 0) {
            setKey("Otro")
            onItemSelected(true)
            return
        }
        if (typeof defaultValue === "number" && defaultValue !== value) {
            const find = tipOptions.find((item) => item.value === defaultValue)
            if (find) {
                setValue(defaultValue)
                setKey(find?.label || "")
            } else {
                setValue(defaultValue)
                setKey("Otro")
            }
        }
    }, [defaultValue, defaultMonto])

    useEffect(() => {
        if (key && key !== "Otro") {
            const find = tipOptions.find((item) => item.label === key)
            if (find) {
                setValue(find.value)
                onChange(find.value)
            }
        }
    }, [amount])

    const handleChange = (label: string, value: number) => {
        if (label === key) {
            setKey("")
            onItemSelected(false)
            onChange(-1)
        } else {
            setKey(label)
            onItemSelected(true)
            onChange(value)
        }
    }

    return (
        <div className="modal-mixto__tip__options">
            <div className="modal-mixto__tip__tabs">
                {tipOptions.map(({ label = "", value = 0 }, index) => (
                    <div
                        key={index}
                        className={`modal-mixto__tip-item ${key === label ? "modal-mixto__tip-item--active" : "modal-mixto__tip-item--default"
                        }`}
                        onClick={() => handleChange(label, value)}
                    >
                        <p className="modal-mixto__tip-item-title">{label}</p>
                        {value > 0 && <p className="modal-mixto__tip-item-label">{`(${getCurrencyFormat(value)})`}</p>}
                    </div>
                ))}
            </div>
            {key === "Otro" && (
                <Controller
                    control={control}
                    name={`items.${index}.propina.monto`}
                    rules={{
                        required: true,
                        validate: (v) => (v ?? 0) > 0,
                    }}
                    render={({
                        field: { onChange, value },
                        fieldState: { error },
                    }) => (
                        <InputMoney
                            value={value}
                            onChange={onChange}
                            placeholder="Monto de propina"
                            label="Monto de propina"
                            icon={DollarCircle}
                            error={!!error}
                            errorhinttext={
                                error?.type === "validate"
                                    ? "Ingresa una propina mayor a 0"
                                    : ""
                            }
                        />
                    )}
                />
            )}
        </div>
    )
}

export function TipSection({ amount = 0, index = 0, control }): JSX.Element {
    const [required, setRequired] = useState<boolean>(false)
    const monto = useWatch({ control, name: `items.${index}.propina.monto` })

    return (
        <Controller
            rules={{ required, validate: required ? (item) => {
                return (item?.value > 0 ? true : undefined)
            } : undefined}}
            control={control}
            name={`items.${index}.propina.porcentaje`}
            render={({ field: { onChange, value }, fieldState: { error } }) => {
                return (
                    <TipOptions
                        index={index}
                        control={control}
                        error={!!error}
                        options={[10, 15]}
                        amount={amount || 0}
                        onChange={onChange}
                        onItemSelected={(value) => {
                            setRequired(value)
                        }}
                        defaultValue={typeof value === "number" ? value : 0}
                        defaultMonto={monto ?? 0}
                    />
                )
            }}
        />
    )
}